import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdSearch, MdClose, MdExpandMore, MdChevronRight } from 'react-icons/md';

/**
 * Country picker in the shape App Store Connect uses: a checkbox per country,
 * grouped by continent with a tri-state checkbox on the group, a search that
 * filters the list, and quick buttons for the groups people actually reach for.
 *
 * The quick groups expand into countries rather than being stored as groups.
 * A retailer who picks "EU" today has agreed to today's EU — if a country joins
 * next year, they should decide whether they deliver there, not find that we
 * decided for them.
 */

// Treaties, not geography, so they live in code and not in the countries table.
const QUICK_GROUPS = [
    { key: 'exyu', labelKey: 'exYu', isos: ['BA', 'HR', 'RS', 'ME', 'MK', 'SI', 'XK'] },
    {
        key: 'eu',
        labelKey: 'eu',
        isos: ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
            'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
            'SI', 'ES', 'SE'],
    },
    {
        key: 'eea',
        labelKey: 'eea',
        isos: ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
            'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
            'SI', 'ES', 'SE', 'IS', 'LI', 'NO'],
    },
];

const CountrySelector = ({
    countries = [],
    value = [],
    onChange,
    homeCountryId = null,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [collapsed, setCollapsed] = useState({});

    const selected = useMemo(() => new Set(value.map(Number)), [value]);

    const byContinent = useMemo(() => {
        const term = search.trim().toLowerCase();
        const groups = new Map();

        countries.forEach((country) => {
            if (term
                && !country.name.toLowerCase().includes(term)
                && !(country.iso || '').toLowerCase().includes(term)) {
                return;
            }
            const key = country.continent || 'Other';
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(country);
        });

        return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [countries, search]);

    const isoToId = useMemo(() => {
        const map = new Map();
        countries.forEach((c) => map.set(c.iso, c.id));
        return map;
    }, [countries]);

    const emit = (next) => onChange?.([...next]);

    const toggleCountry = (id) => {
        if (disabled) return;
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        emit(next);
    };

    const toggleContinent = (list, allOn) => {
        if (disabled) return;
        const next = new Set(selected);
        list.forEach((c) => (allOn ? next.delete(c.id) : next.add(c.id)));
        emit(next);
    };

    const addGroup = (isos) => {
        if (disabled) return;
        const next = new Set(selected);
        isos.forEach((iso) => {
            const id = isoToId.get(iso);
            if (id) next.add(id);
        });
        emit(next);
    };

    const selectedCountries = useMemo(
        () => countries.filter((c) => selected.has(c.id)),
        [countries, selected],
    );

    return (
        <Wrapper $disabled={disabled}>
            <Toolbar>
                <SearchBox>
                    <MdSearch size={18} />
                    <input
                        type="search"
                        value={search}
                        disabled={disabled}
                        placeholder={t('Settings.Delivery.searchPlaceholder')}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <ClearSearch type="button" onClick={() => setSearch('')} aria-label={t('Common.clear', { defaultValue: 'Clear' })}>
                            <MdClose size={16} />
                        </ClearSearch>
                    )}
                </SearchBox>
                <Counter>
                    <strong>{selected.size}</strong> / {countries.length}
                </Counter>
            </Toolbar>

            <QuickRow>
                <span>{t('Settings.Delivery.quickGroups')}</span>
                {QUICK_GROUPS.map((group) => (
                    <QuickButton
                        key={group.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => addGroup(group.isos)}
                    >
                        + {t(`Settings.Delivery.groups.${group.labelKey}`)}
                    </QuickButton>
                ))}
                {selected.size > 0 && (
                    <QuickButton type="button" $ghost disabled={disabled} onClick={() => emit(new Set())}>
                        {t('Settings.Delivery.clearAll')}
                    </QuickButton>
                )}
            </QuickRow>

            <List>
                {byContinent.length === 0 && (
                    <Empty>{t('Settings.Delivery.noMatches', { term: search })}</Empty>
                )}

                {byContinent.map(([continent, list]) => {
                    const chosen = list.filter((c) => selected.has(c.id)).length;
                    const allOn = chosen === list.length && list.length > 0;
                    const isCollapsed = collapsed[continent];

                    return (
                        <Group key={continent}>
                            <GroupHead>
                                <Collapse
                                    type="button"
                                    onClick={() => setCollapsed((p) => ({ ...p, [continent]: !p[continent] }))}
                                    aria-label={continent}
                                >
                                    {isCollapsed ? <MdChevronRight size={18} /> : <MdExpandMore size={18} />}
                                </Collapse>
                                <GroupCheckbox
                                    type="checkbox"
                                    checked={allOn}
                                    disabled={disabled}
                                    // Partly-chosen continents read as indeterminate rather
                                    // than unchecked, which would suggest nothing is selected.
                                    ref={(el) => {
                                        if (el) el.indeterminate = chosen > 0 && !allOn;
                                    }}
                                    onChange={() => toggleContinent(list, allOn)}
                                />
                                <GroupName>{t(`Settings.Delivery.continents.${continent}`, { defaultValue: continent })}</GroupName>
                                <GroupCount>{chosen}/{list.length}</GroupCount>
                            </GroupHead>

                            {!isCollapsed && (
                                <Countries>
                                    {list.map((country) => {
                                        const isHome = country.id === homeCountryId;
                                        return (
                                            <CountryRow key={country.id} $home={isHome}>
                                                <input
                                                    type="checkbox"
                                                    checked={isHome || selected.has(country.id)}
                                                    // The home country is always covered, so it is
                                                    // shown ticked and locked instead of letting
                                                    // someone "turn off" their own country.
                                                    disabled={disabled || isHome}
                                                    onChange={() => toggleCountry(country.id)}
                                                />
                                                {country.flag && <Flag src={country.flag} alt="" loading="lazy" />}
                                                <CountryName>{country.name}</CountryName>
                                                <Iso>{country.iso}</Iso>
                                                {isHome && <HomeTag>{t('Settings.Delivery.homeCountry')}</HomeTag>}
                                            </CountryRow>
                                        );
                                    })}
                                </Countries>
                            )}
                        </Group>
                    );
                })}
            </List>

            {selectedCountries.length > 0 && (
                <Chips>
                    {selectedCountries.map((country) => (
                        <Chip key={country.id}>
                            {country.flag && <Flag src={country.flag} alt="" />}
                            {country.iso}
                            <ChipRemove type="button" disabled={disabled} onClick={() => toggleCountry(country.id)}>
                                <MdClose size={13} />
                            </ChipRemove>
                        </Chip>
                    ))}
                </Chips>
            )}
        </Wrapper>
    );
};

export default CountrySelector;

const Wrapper = styled.div`
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  padding: 0 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  color: #6b7280;

  &:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-bg);
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    padding: 0.625rem 0;
    font-size: 0.875rem;
    background: transparent;
    color: #111827;

    &::-webkit-search-cancel-button { display: none; }
  }
`;

const ClearSearch = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
`;

const Counter = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;

  strong {
    color: var(--color-primary);
    font-size: 0.9375rem;
  }
`;

const QuickRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.875rem;

  > span {
    font-size: 0.75rem;
    color: #6b7280;
    margin-right: 0.125rem;
  }
`;

const QuickButton = styled.button`
  border: 1px solid ${({ $ghost }) => ($ghost ? '#e5e7eb' : 'var(--color-primary)')};
  color: ${({ $ghost }) => ($ghost ? '#6b7280' : 'var(--color-primary)')};
  background: ${({ $ghost }) => ($ghost ? '#fff' : 'var(--color-primary-bg)')};
  border-radius: 999px;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ $ghost }) => ($ghost ? '#f9fafb' : 'var(--color-primary-bg-soft)')};
  }

  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const List = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  /* Fixed height with its own scroll: the page keeps a stable length whatever
     the search matches, and the Save button below never moves out of reach. */
  max-height: 420px;
  overflow-y: auto;
  background: #fff;
`;

const Group = styled.div`
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
`;

const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Collapse = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  color: #6b7280;
`;

const GroupCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--color-primary);
  cursor: pointer;
`;

const GroupName = styled.div`
  font-weight: 700;
  font-size: 0.8125rem;
  color: #111827;
  flex: 1;
`;

const GroupCount = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const Countries = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.125rem 0.5rem;
  padding: 0.5rem 0.875rem 0.75rem;
`;

const CountryRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 8px;
  cursor: ${({ $home }) => ($home ? 'default' : 'pointer')};
  font-size: 0.8125rem;

  &:hover { background: #f9fafb; }

  input {
    width: 15px;
    height: 15px;
    accent-color: var(--color-primary);
    cursor: inherit;
  }
`;

const Flag = styled.img`
  width: 18px;
  height: 13px;
  object-fit: cover;
  border-radius: 2px;
  flex: none;
`;

const CountryName = styled.span`
  flex: 1;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Iso = styled.span`
  font-size: 0.6875rem;
  color: #9ca3af;
`;

const HomeTag = styled.span`
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--color-primary);
  background: var(--color-primary-bg);
  border-radius: 999px;
  padding: 0.125rem 0.375rem;
`;

const Empty = styled.div`
  padding: 1.75rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.8125rem;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.75rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.375rem 0.25rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
`;

const ChipRemove = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  padding: 0;

  &:hover { color: #dc2626; }
`;
